import { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { MessageCircle, Flag, Trash2 } from 'lucide-react';

const REACTIONS = [
  { key: 'pray', emoji: '🙏', label: 'Praying', color: '#78A18E' },
  { key: 'amen', emoji: '❤️', label: 'Amen', color: '#E57A44' },
  { key: 'grateful', emoji: '✨', label: 'Grateful', color: '#E8C265' },
];

export default function EngagementBar({ parentType, parentId }) {
  const { user } = useAuth();
  const [reactions, setReactions] = useState({ counts: { pray: 0, amen: 0, grateful: 0 }, mine: [] });
  const [comments, setComments] = useState([]);
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [isAnon, setIsAnon] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadReactions();
    loadCommentCount();
  }, [parentType, parentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadReactions = async () => {
    try {
      const { data } = await api.get('/engagement/reactions', { params: { parent_type: parentType, parent_id: parentId } });
      setReactions(data);
    } catch {}
  };

  const loadCommentCount = async () => {
    try {
      const { data } = await api.get('/engagement/comments', { params: { parent_type: parentType, parent_id: parentId } });
      setComments(data);
    } catch {}
  };

  const toggleReaction = async (reactionKey) => {
    if (!user) {
      toast.error('Please sign in to react');
      return;
    }
    try {
      const { data } = await api.post('/engagement/reactions', { parent_type: parentType, parent_id: parentId, reaction_type: reactionKey });
      setReactions({ counts: data.counts, mine: data.mine });
    } catch {
      toast.error('Could not save reaction');
    }
  };

  const openComments = async () => {
    setShowComments(v => !v);
    if (!showComments) {
      setLoading(true);
      await loadCommentCount();
      setLoading(false);
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!user) { toast.error('Please sign in to encourage'); return; }
    if (!commentText.trim()) return;
    setSubmitting(true);
    try {
      await api.post('/engagement/comments', {
        parent_type: parentType,
        parent_id: parentId,
        content: commentText.trim(),
        is_anonymous: isAnon
      });
      setCommentText('');
      loadCommentCount();
      toast.success('Your encouragement is shared');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not post encouragement');
    } finally {
      setSubmitting(false);
    }
  };

  const reportComment = async (id) => {
    if (!user) { toast.error('Please sign in to report'); return; }
    if (!window.confirm('Report this comment to Genesis for review?')) return;
    try {
      const { data } = await api.post(`/engagement/comments/${id}/report`);
      toast.success(data.message || 'Reported');
      loadCommentCount();
    } catch {
      toast.error('Could not report');
    }
  };

  const deleteMyComment = async (id) => {
    if (!window.confirm('Delete your encouragement?')) return;
    try {
      await api.delete(`/engagement/comments/${id}`);
      loadCommentCount();
      toast.success('Deleted');
    } catch {
      toast.error('Could not delete');
    }
  };

  return (
    <div className="mt-4 pt-4" style={{ borderTop: '1px solid #F3EFE7' }} data-testid={`engagement-bar-${parentId}`}>
      <div className="flex items-center gap-2 flex-wrap">
        {REACTIONS.map((r) => {
          const active = reactions.mine.includes(r.key);
          const count = reactions.counts[r.key] || 0;
          return (
            <button
              key={r.key}
              onClick={() => toggleReaction(r.key)}
              aria-pressed={active}
              aria-label={`${r.label} — ${count} so far`}
              className="reaction-btn inline-flex items-center gap-1.5 px-3.5 rounded-full transition-all duration-200 md:hover:-translate-y-0.5 active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
              style={{
                fontFamily: 'Manrope, sans-serif',
                minHeight: '44px',  // Apple HIG / Google MD3 minimum tap target
                fontSize: '13px',
                lineHeight: 1,
                backgroundColor: active ? `${r.color}18` : '#FFFFFF',
                color: active ? r.color : '#595553',
                fontWeight: active ? 700 : 600,
                border: active ? `1.5px solid ${r.color}55` : '1.5px solid #E6E2D8',
                boxShadow: active ? `0 2px 8px ${r.color}25` : 'none',
                '--tw-ring-color': `${r.color}66`,
              }}
              data-testid={`reaction-${r.key}-${parentId}`}
            >
              <span aria-hidden style={{ fontSize: '16px', transform: active ? 'scale(1.12)' : 'scale(1)', transition: 'transform 200ms ease' }}>{r.emoji}</span>
              <span>{r.label}</span>
              {count > 0 && (
                <span
                  className="ml-1 inline-flex items-center justify-center rounded-full"
                  style={{
                    minWidth: '20px', height: '20px', padding: '0 6px',
                    backgroundColor: active ? r.color : '#F3EFE7',
                    color: active ? '#FFFFFF' : '#595553',
                    fontSize: '11px', fontWeight: 800, letterSpacing: '0.02em',
                  }}
                >{count}</span>
              )}
            </button>
          );
        })}
        <button
          onClick={openComments}
          aria-pressed={showComments}
          className="inline-flex items-center gap-1.5 px-3.5 rounded-full transition-all duration-200 md:hover:-translate-y-0.5 active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 sm:ml-auto"
          style={{
            fontFamily: 'Manrope, sans-serif',
            minHeight: '44px',
            fontSize: '13px',
            lineHeight: 1,
            backgroundColor: showComments ? '#5B7C9918' : '#FFFFFF',
            color: showComments ? '#5B7C99' : '#595553',
            fontWeight: showComments ? 700 : 600,
            border: showComments ? '1.5px solid #5B7C9955' : '1.5px solid #E6E2D8',
            boxShadow: showComments ? '0 2px 8px #5B7C9925' : 'none',
            '--tw-ring-color': '#5B7C9966',
          }}
          data-testid={`toggle-comments-${parentId}`}
        >
          <MessageCircle className="w-4 h-4" style={{ strokeWidth: 2.2 }} />
          <span>Encourage</span>
          {comments.length > 0 && (
            <span
              className="ml-1 inline-flex items-center justify-center rounded-full"
              style={{
                minWidth: '20px', height: '20px', padding: '0 6px',
                backgroundColor: showComments ? '#5B7C99' : '#F3EFE7',
                color: showComments ? '#FFFFFF' : '#595553',
                fontSize: '11px', fontWeight: 800,
              }}
            >{comments.length}</span>
          )}
        </button>
      </div>

      {showComments && (
        <div className="mt-4" data-testid={`comments-panel-${parentId}`}>
          {user ? (
            <form onSubmit={submitComment} className="mb-3">
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                maxLength={300}
                rows={2}
                placeholder="A short word of encouragement… (max 300 chars)"
                className="w-full px-3 py-2 rounded-xl border text-sm"
                style={{ fontFamily: 'Lora, serif', backgroundColor: '#FFFFFF', borderColor: '#E6E2D8', color: '#2C2A29' }}
                data-testid={`comment-input-${parentId}`}
              />
              <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
                <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>
                  <input type="checkbox" checked={isAnon} onChange={(e) => setIsAnon(e.target.checked)} data-testid={`comment-anon-${parentId}`} />
                  <span>Post as Anonymous</span>
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>{commentText.length}/300</span>
                  <button
                    type="submit"
                    disabled={submitting || !commentText.trim()}
                    className="px-4 py-1.5 rounded-full text-xs disabled:opacity-50"
                    style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#78A18E', color: '#FDFBF7', fontWeight: 600 }}
                    data-testid={`comment-submit-${parentId}`}
                  >{submitting ? 'Sending…' : 'Send'}</button>
                </div>
              </div>
            </form>
          ) : (
            <div className="p-3 rounded-xl mb-3 text-center text-sm" style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#F3EFE7', color: '#595553' }}>
              <Link to="/login" className="font-semibold" style={{ color: '#E57A44' }}>Sign in</Link> to react or encourage others.
            </div>
          )}

          {loading && <p className="text-xs text-center py-2" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>Loading…</p>}

          {!loading && comments.length === 0 && (
            <p className="text-xs text-center py-2" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>Be the first to encourage.</p>
          )}

          {comments.map((c) => (
            <div key={c.id} className="p-3 rounded-xl mb-2" style={{ backgroundColor: c.is_hidden ? '#FDECE4' : '#FDFBF7', border: c.is_hidden ? '1px dashed #E57A44' : '1px solid #F3EFE7' }} data-testid={`comment-${c.id}`}>
              <p className="text-sm mb-1" style={{ fontFamily: 'Lora, serif', color: '#2C2A29', lineHeight: 1.6 }}>
                {c.is_hidden ? <em style={{ color: '#8C867D' }}>[This comment is under review]</em> : c.content}
              </p>
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
                  — <span style={{ color: '#78A18E', fontWeight: 600 }}>{c.display_name}</span> · {new Date(c.created_at).toLocaleDateString()}
                </p>
                <div className="flex items-center gap-1">
                  {c.is_mine && (
                    <button onClick={() => deleteMyComment(c.id)} className="p-1 rounded-full hover:bg-white text-xs" style={{ color: '#8C867D' }} aria-label="Delete" data-testid={`delete-my-comment-${c.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {!c.is_mine && user && (
                    <button onClick={() => reportComment(c.id)} className="p-1 rounded-full hover:bg-white text-xs" style={{ color: '#8C867D' }} aria-label="Report" data-testid={`report-comment-${c.id}`}>
                      <Flag className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
